import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { tradeService } from '../services/tradeService';
import { userService } from '../services/userService';
import { useSocket } from '../context/SocketContext';
import UserLanguageSwitcher from '../components/common/UserLanguageSwitcher';
import './MajesticCommon.css';
import './TradePage.css';

const PlaceOrderPage = () => {
    const { symbol: assetSymbol, direction: initialDirection } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const socket = useSocket();

    const [tradeType, setTradeType] = useState(initialDirection || 'buy');
    const [amount, setAmount] = useState('');
    const [duration, setDuration] = useState(120);
    const [leverage, setLeverage] = useState(1);
    const [profitRate, setProfitRate] = useState(30); // Default 120s corresponds to 30% profit
    const [balance, setBalance] = useState(0);
    const [priceData, setPriceData] = useState({ price: '0.00' });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const data = await userService.getProfile();
                if (data.success) {
                    setBalance(data.user.accountBalance);
                }
            } catch (err) { console.error(err); }
        };
        fetchProfile();
    }, []);

    useEffect(() => {
        if (!socket) return;
        const handleBalanceUpdate = (data) => {
            if (data.balance !== undefined) {
                setBalance(parseFloat(data.balance));
            }
        };
        socket.on('balance_update', handleBalanceUpdate);
        return () => socket.off('balance_update', handleBalanceUpdate);
    }, [socket]);

    // Local price feed implementation
    useEffect(() => {
        let isMounted = true;
        let ws = null;
        let simulationInterval = null;
        const apiSymbol = (assetSymbol || 'BTCUSDT').toUpperCase();

        const isMock = apiSymbol.includes('GOLD') || apiSymbol.includes('EURO');

        const fetchInitialPrice = async () => {
            try {
                if (isMock) {
                    const basePrice = apiSymbol.includes('GOLD') ? 4900 : 4500; // Standardized gold price
                    if (isMounted) setPriceData({ price: basePrice.toFixed(2) });
                } else {
                    const response = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${apiSymbol}`);
                    const data = await response.json();
                    if (isMounted && data.price) {
                        setPriceData({ price: parseFloat(data.price).toFixed(2) });
                    }
                }
            } catch (e) {
                console.error('Initial price fetch error:', e);
                toast.error(t('price_fetch_error', 'Failed to fetch price. Please try again.'));
            }
        };

        fetchInitialPrice();

        if (isMock) {
            simulationInterval = setInterval(() => {
                if (!isMounted) return;
                setPriceData(prev => {
                    const currentPrice = parseFloat(prev.price);
                    const volatility = currentPrice * 0.0001;
                    const change = (Math.random() - 0.5) * volatility;
                    return { price: (currentPrice + change).toFixed(2) };
                });
            }, 2000);
        } else {
            const wsUrl = `wss://stream.binance.com:9443/ws/${assetSymbol.toLowerCase()}@ticker`;
            ws = new WebSocket(wsUrl);
            ws.onmessage = (event) => {
                if (!isMounted) return;
                try {
                    const data = JSON.parse(event.data);
                    if (data.c) {
                        setPriceData({ price: parseFloat(data.c).toFixed(2) });
                    }
                } catch (e) { }
            };
        }

        return () => {
            isMounted = false;
            if (ws) ws.close();
            if (simulationInterval) clearInterval(simulationInterval);
        };
    }, [assetSymbol]);

    const handlePlaceTrade = async (e) => {
        e.preventDefault();
        if (!amount || parseFloat(amount) <= 0) {
            toast.error(t('msg_enter_valid_amount', 'Please enter a valid amount'));
            return;
        }

        try {
            if (parseFloat(priceData.price) <= 0) {
                toast.error(t('msg_waiting_for_price', 'Waiting for real-time price...'));
                return;
            }

            const data = await tradeService.placeTrade({
                pair: assetSymbol,
                type: tradeType,
                amount: parseFloat(amount),
                entryPrice: parseFloat(priceData.price),
                duration: duration,
                leverage: leverage,
                profitRate: profitRate
            });

            if (data.success) {
                toast.success(t('trade_placed_success', 'Trade placed successfully!'));

                // Use the actual entry price from the server if returned, else use local priceData
                const entryPrice = data.trade?.entryPrice || parseFloat(priceData.price);

                // Redirect back to trade page with state to show confirmation modal
                // Save state to sessionStorage to survive navigation
                const tradeState = {
                    showConfirmation: true,
                    tradeData: {
                        direction: tradeType,
                        amount: parseFloat(amount),
                        purchasePrice: entryPrice,
                        leverage: leverage,
                        duration: duration,
                        timestamp: Date.now()
                    }
                };
                sessionStorage.setItem('pendingTradeConfirmation', JSON.stringify(tradeState));

                // Go back to previous page
                navigate(-1);
            }
        } catch (error) {
            toast.error(t(error.message || 'trade_failed'));
        }
    };

    return (
        <div className="majestic-page-wrapper trade-dashboard-wrapper" style={{ minHeight: '100vh', background: '#0b1118' }}>
            {/* Header */}
            <div className="majestic-header d-flex align-items-center px-3 py-3 justify-content-between" style={{ background: '#0b1118', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ width: '40px' }}>
                    <i className="bi bi-chevron-left text-white fs-4 cursor-pointer" onClick={() => navigate(-1)}></i>
                </div>
                <h1 className="text-white m-0 fw-bold" style={{ fontSize: '18px' }}>
                    {t('place_order', 'Place Order')}
                </h1>
                <div style={{ width: '40px' }} className="d-flex justify-content-end">
                    <UserLanguageSwitcher />
                </div>
            </div>

            <div className="place-order-body p-4">
                <Form onSubmit={handlePlaceTrade}>

                    <div className="section-label">{t('select_period', 'Select Duration')}</div>
                    <div className="period-grid">
                        {[
                            { duration: 30, profit: 10 },
                            { duration: 60, profit: 20 },
                            { duration: 120, profit: 30 },
                            { duration: 300, profit: 40 },
                            { duration: 600, profit: 50 },
                            { duration: 900, profit: 60 },
                            { duration: 1200, profit: 70 },
                            { duration: 1500, profit: 80 },
                            { duration: 1800, profit: 90 },
                            { duration: 2400, profit: 95 },
                            { duration: 3000, profit: 98 },
                            { duration: 3600, profit: 100 }
                        ].map((opt) => (
                            <div
                                key={opt.duration}
                                className={`trade-option-card ${duration === opt.duration ? 'active' : ''}`}
                                onClick={() => {
                                    setDuration(opt.duration);
                                    setProfitRate(opt.profit);
                                }}
                            >
                                <div className="option-main-text">
                                    {opt.duration === 3600 ? t('1_hour', '1 hour') : (opt.duration < 60 ? `${opt.duration}s` : `${Math.floor(opt.duration / 60)} ${t('minute', 'minute')}`)}
                                </div>
                                <div className="option-sub-text">{t('profit', 'profit')} {opt.profit}%</div>
                            </div>
                        ))}
                    </div>

                    {/* Select Leverage Ratio */}
                    <div className="section-label">{t('select_leverage', 'Select Leverage Ratio')}</div>
                    <div className="leverage-grid">
                        {[1, 2, 3, 5, 10, 20, 30].map((lev) => (
                            <div
                                key={lev}
                                className={`leverage-option ${leverage === lev ? 'active' : ''}`}
                                onClick={() => setLeverage(lev)}
                            >
                                x{lev}
                            </div>
                        ))}
                    </div>

                    {/* Investment Amount */}
                    <label htmlFor="amount" className="section-label">{t('enter_investment', 'Please enter investment amount')}</label>
                    <div className="investment-container">
                        <div className="investment-input-wrapper">
                            <Form.Control
                                id="amount"
                                name="amount"
                                type="number"
                                placeholder={t('investment_amount_placeholder', 'Investment Amount')}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {/* Balance Row */}
                    <div className="balance-row mb-3">
                        <div className="balance-label">{t('balance', 'Balance')}</div>
                        <div className="balance-amount">{parseFloat(balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>

                    {/* Order Summary Row */}
                    <div className="order-summary-row mb-4">
                        <div className="summary-item">
                            <div className="summary-label">{t('name', 'Name')}</div>
                            <div className="summary-value" style={{ fontSize: '10px' }}>{assetSymbol?.toLowerCase()}</div>
                        </div>
                        <div className="summary-item">
                            <div className="summary-label">{t('direction', 'Direction')}</div>
                            <div className="summary-value">{tradeType === 'buy' ? t('buy_up', 'Buy Up') : t('buy_down', 'Buy Down')}</div>
                        </div>
                        <div className="summary-item">
                            <div className="summary-label">{t('current_price', 'Current Price')}</div>
                            <div className="summary-value">{priceData.price === '0.00' ? '---' : priceData.price}</div>
                        </div>
                        <div className="summary-item">
                            <div className="summary-label">{t('amount', 'Amount')}</div>
                            <div className="summary-value">{amount || 0}</div>
                        </div>
                    </div>

                    <Button
                        className="btn-submit-order"
                        type="submit"
                        style={{ height: '55px' }}
                    >
                        {t('submit', 'Submit')}
                    </Button>
                </Form>
            </div>
        </div>
    );
};

export default PlaceOrderPage;
